// PictureChoice (the price book round, DEC-019; concept C's Swap and goes-with): a choice drawn as
// pictures. Two kinds, one drawing:
// - one of several (Swap: trade a line for its neighbour): a radio group, the one that's on framed
//   in ink, each with its words ("pull-down") and what the trade does ("This one", "−$160");
// - things to add (Usually goes with it): each a button framed dashed (suggested, waiting on you),
//   its picture, name, what it adds and how often it's sold with it, and a +.
// The arrow keys move through one of several; Tab and Enter through things to add. Not for words
// alone (ChoiceGroup) or a whole catalogue (PictureGrid). Reads only --picchoice-*; Picture reads its own.
import { Radio, RadioGroup, Label } from 'react-aria-components';
import { Glyph } from '../Glyph/Glyph.tsx';
import { Picture, type PictureSrc, type PictureState } from '../Picture/Picture.tsx';
import './PictureChoice.css';

export interface PictureOption {
  id: string;
  /** Its words: "pull-down", or the item's name. */
  label: string;
  picture?: PictureSrc | null;
  pictureState?: PictureState;
  /** What choosing it does: "This one", "−$160", "+$185 · on 64 %". */
  line?: string;
  /** Muted, under the line: "in option A". */
  note?: string;
  isDisabled?: boolean;
}

interface Common {
  /** What the choice is ("Swap the faucet for", "Usually goes with it"). */
  label: string;
  options: PictureOption[];
  /** Keeps the label for screen readers only, where the line above says it. */
  hideLabel?: boolean;
}
export type PictureChoiceProps =
  | (Common & { mode: 'one'; value: string; onChange: (id: string) => void })
  | (Common & { mode: 'add'; onAdd: (id: string) => void; addLabel?: string });

function Face({ o, size }: { o: PictureOption; size: 'md' | 'sm' }) {
  return (
    <>
      <Picture src={o.picture} state={o.pictureState} size={size} />
      <span className="fl-picchoice__words">
        <span className="fl-picchoice__label">{o.label}</span>
        {o.line ? <span className="fl-picchoice__line">{o.line}</span> : null}
        {o.note ? <span className="fl-picchoice__note">{o.note}</span> : null}
      </span>
    </>
  );
}

export function PictureChoice(props: PictureChoiceProps) {
  const { label, options, hideLabel } = props;
  if (props.mode === 'one')
    return (
      <RadioGroup
        className="fl-picchoice"
        data-mode="one"
        value={props.value}
        onChange={props.onChange}
        orientation="horizontal"
      >
        <Label className="fl-picchoice__head" data-hidden={hideLabel ? '' : undefined}>
          {label}
        </Label>
        <div className="fl-picchoice__list">
          {options.map((o) => (
            <Radio key={o.id} value={o.id} className="fl-picchoice__one" isDisabled={o.isDisabled}>
              <Face o={o} size="md" />
            </Radio>
          ))}
        </div>
      </RadioGroup>
    );
  const { onAdd, addLabel = 'Add' } = props;
  return (
    <section className="fl-picchoice" data-mode="add" aria-label={label}>
      <p className="fl-picchoice__head" data-hidden={hideLabel ? '' : undefined} aria-hidden="true">
        {label}
      </p>
      <ul className="fl-picchoice__adds">
        {options.map((o) => (
          <li key={o.id}>
            <button
              type="button"
              className="fl-picchoice__add"
              disabled={o.isDisabled}
              aria-label={`${addLabel}: ${o.label}${o.line ? `, ${o.line}` : ''}`}
              onClick={() => onAdd(o.id)}
            >
              <Face o={o} size="sm" />
              <Glyph name="plus" className="fl-picchoice__glyph" />
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
